/**
 * One Piece Arc Data
 * For tracking progress and providing arc-specific recaps
 * One Piece is the GOAT - deserves special treatment!
 */

export interface OnePieceArc {
  name: string;
  saga: string;
  episodes: [number, number | 'ongoing'];
  manga?: [number, number | 'ongoing'];
  keyEvents: string[];
}

export const ONE_PIECE_CONFIG = {
  tmdbId: 37854,
  title: 'One Piece',

  // Anime streaming sources (free, from FMHY)
  streamingSources: {
    hianime: (ep: number) => `https://hianime.to/watch/one-piece-100?ep=${ep}`,
    nineanime: (ep: number) => `https://9animetv.to/watch/one-piece-100?ep=${ep}`,
    aniwatch: (ep: number) => `https://aniwatchtv.to/watch/one-piece-100?ep=${ep}`,
  },

  // Default source
  defaultSource: 'hianime' as const,

  wikiaUrl: 'https://onepiece.fandom.com',

  // Preferred YouTube channels for recaps
  recapChannels: [
    'GrandLineReview',
    'Tekking101',
    'JoyBoy Theories',
    'Ohara',
    'Mr Morj',
  ],
};

export const ONE_PIECE_ARCS: OnePieceArc[] = [
  // East Blue Saga
  { name: 'Romance Dawn', saga: 'East Blue', episodes: [1, 3], keyEvents: ['Luffy begins journey', 'Meets Coby'] },
  { name: 'Orange Town', saga: 'East Blue', episodes: [4, 8], keyEvents: ['Meets Nami', 'Defeats Buggy'] },
  { name: 'Syrup Village', saga: 'East Blue', episodes: [9, 18], keyEvents: ['Meets Usopp', 'Gets Going Merry'] },
  { name: 'Baratie', saga: 'East Blue', episodes: [19, 30], keyEvents: ['Meets Sanji', 'Don Krieg fight'] },
  { name: 'Arlong Park', saga: 'East Blue', episodes: [31, 45], keyEvents: ['Nami backstory', 'Defeats Arlong'] },
  { name: 'Loguetown', saga: 'East Blue', episodes: [46, 53], keyEvents: ['Roger execution platform', 'Enters Grand Line'] },

  // Alabasta Saga
  { name: 'Reverse Mountain', saga: 'Alabasta', episodes: [62, 63], keyEvents: ['Meets Laboon', 'Enters Grand Line'] },
  { name: 'Whiskey Peak', saga: 'Alabasta', episodes: [64, 67], keyEvents: ['Meets Vivi', 'Baroque Works revealed'] },
  { name: 'Little Garden', saga: 'Alabasta', episodes: [70, 77], keyEvents: ['Giants Dorry & Brogy'] },
  { name: 'Drum Island', saga: 'Alabasta', episodes: [78, 91], keyEvents: ['Meets Chopper', 'Dr. Hiriluk'] },
  { name: 'Alabasta', saga: 'Alabasta', episodes: [92, 130], keyEvents: ['Defeats Crocodile', 'Vivi farewell'] },

  // Sky Island Saga
  { name: 'Jaya', saga: 'Sky Island', episodes: [144, 152], keyEvents: ['Meets Bellamy', 'Blackbeard intro'] },
  { name: 'Skypiea', saga: 'Sky Island', episodes: [153, 195], keyEvents: ['Defeats Enel', 'Golden Bell'] },

  // Water 7 Saga
  { name: 'Long Ring Long Land', saga: 'Water 7', episodes: [207, 219], keyEvents: ['Davy Back Fight', 'Aokiji encounter'] },
  { name: 'Water 7', saga: 'Water 7', episodes: [229, 263], keyEvents: ['Robin leaves', 'Merry broken', 'CP9 revealed'] },
  { name: 'Enies Lobby', saga: 'Water 7', episodes: [264, 312], keyEvents: ['Rescues Robin', 'Gear Second/Third', 'Merry funeral'] },
  { name: 'Post-Enies Lobby', saga: 'Water 7', episodes: [313, 325], keyEvents: ['Thousand Sunny', 'Franky joins'] },

  // Thriller Bark Saga
  { name: 'Thriller Bark', saga: 'Thriller Bark', episodes: [337, 381], keyEvents: ['Brook joins', 'Defeats Moria', 'Kuma appears'] },

  // Summit War Saga
  { name: 'Sabaody Archipelago', saga: 'Summit War', episodes: [385, 405], keyEvents: ['Supernovas', 'Crew separated', 'Rayleigh'] },
  { name: 'Amazon Lily', saga: 'Summit War', episodes: [408, 421], keyEvents: ['Meets Hancock', 'Learns about Ace'] },
  { name: 'Impel Down', saga: 'Summit War', episodes: [422, 458], keyEvents: ['Prison break', 'Meets Jinbe', 'Ivankov'] },
  { name: 'Marineford', saga: 'Summit War', episodes: [459, 489], keyEvents: ['War of the Best', 'Ace dies', 'Whitebeard dies'] },
  { name: 'Post-War', saga: 'Summit War', episodes: [490, 516], keyEvents: ['3D2Y', 'Timeskip begins'] },

  // Fish-Man Island Saga
  { name: 'Return to Sabaody', saga: 'Fish-Man Island', episodes: [517, 522], keyEvents: ['Crew reunites', 'New bounties'] },
  { name: 'Fish-Man Island', saga: 'Fish-Man Island', episodes: [523, 574], keyEvents: ['Defeats Hody', 'Shirahoshi', 'Joy Boy mention'] },

  // Dressrosa Saga
  { name: 'Punk Hazard', saga: 'Dressrosa', episodes: [579, 625], keyEvents: ['Law alliance', 'Caesar captured', 'SMILE revealed'] },
  { name: 'Dressrosa', saga: 'Dressrosa', episodes: [629, 746], keyEvents: ['Gear Fourth', 'Defeats Doflamingo', 'Grand Fleet formed'] },

  // Whole Cake Island Saga
  { name: 'Zou', saga: 'Whole Cake Island', episodes: [751, 779], keyEvents: ['Minks', 'Raizo safe', 'Road Poneglyphs'] },
  { name: 'Whole Cake Island', saga: 'Whole Cake Island', episodes: [783, 877], keyEvents: ['Sanji wedding', 'Big Mom chase', 'Jinbe joins'] },

  // Wano Saga
  { name: 'Reverie', saga: 'Wano', episodes: [878, 889], keyEvents: ['World Government meeting', 'Sabo news'] },
  { name: 'Wano', saga: 'Wano', episodes: [890, 1085], keyEvents: ['Defeats Kaido', 'Gear Fifth', 'Nika revealed', 'New Emperors'] },

  // Final Saga
  { name: 'Egghead', saga: 'Final Saga', episodes: [1086, 'ongoing'], keyEvents: ['Vegapunk', 'Void Century clues', 'Five Elders'] },
];

// Get arc by episode number
export function getArcByEpisode(episode: number): OnePieceArc | undefined {
  return ONE_PIECE_ARCS.find(arc => {
    const [start, end] = arc.episodes;
    if (end === 'ongoing') {
      return episode >= start;
    }
    return episode >= start && episode <= end;
  });
}

// Get saga by episode number
export function getSagaByEpisode(episode: number): string {
  const arc = getArcByEpisode(episode);
  return arc?.saga || 'Unknown';
}

// Get all arcs in a saga
export function getArcsBySaga(saga: string): OnePieceArc[] {
  return ONE_PIECE_ARCS.filter(arc => arc.saga === saga);
}

// Get unique sagas
export function getAllSagas(): string[] {
  return [...new Set(ONE_PIECE_ARCS.map(arc => arc.saga))];
}

// Get progress percentage (approximate)
export function getProgressPercentage(episode: number): number {
  const latestEpisode = 1122; // Update this periodically
  return Math.min(100, Math.round((episode / latestEpisode) * 100));
}

// Get recap search queries based on episode
export function getRecapQueries(episode: number): string[] {
  const arc = getArcByEpisode(episode);
  const saga = getSagaByEpisode(episode);

  return [
    `One Piece ${arc?.name || ''} arc recap`,
    `One Piece ${saga} saga recap explained`,
    `One Piece episode ${episode} recap`,
    'One Piece story so far explained',
  ];
}

// One Piece specific recap types
export type OnePieceRecapType =
  | 'full_series'
  | 'current_arc'
  | 'current_saga'
  | 'last_50_episodes'
  | 'power_system'
  | 'character';

export function getRecapQueryByType(type: OnePieceRecapType, episode?: number): string {
  const arc = episode ? getArcByEpisode(episode) : undefined;

  const queries: Record<OnePieceRecapType, string> = {
    full_series: 'One Piece complete story recap explained all sagas',
    current_arc: `One Piece ${arc?.name || 'Egghead'} arc recap explained`,
    current_saga: `One Piece ${arc?.saga || 'Final Saga'} recap everything`,
    last_50_episodes: 'One Piece recent episodes recap 2024 2025',
    power_system: 'One Piece Haki Devil Fruits Nika explained',
    character: 'One Piece Straw Hat crew journey recap',
  };

  return queries[type];
}
